import { useEffect, useState } from "react";
import {
  OrganizationResponse,
  OrganizationType,
  UpdateOrganizationRequest,
} from "../../../models/OrganizationModels";
import "./OrganizationCreateModal.css";

export type OrganizationModalMode = "create" | "view" | "edit";

const ORGANIZATION_TYPE_OPTIONS: { value: OrganizationType; label: string }[] = [
  { value: OrganizationType.Dental, label: "Dental" },
  { value: OrganizationType.SkinCare, label: "Skin Care" },
  { value: OrganizationType.BeautyParlor, label: "Beauty Parlor" },
];

interface OrganizationModalProps {
  isOpen: boolean;
  mode: OrganizationModalMode;
  organization: OrganizationResponse | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (form: OrganizationFormValues) => Promise<void>;
}

export interface OrganizationFormValues {
  name: string;
  slug: string;
  organizationType: OrganizationType;
  phone: string;
  email: string;
  description: string;
  address: string;
  city: string;
  area: string;
  latitude: string;
  longitude: string;
  googleMapsUrl: string;
  websiteTitle: string;
  websiteTagline: string;
  facebookUrl: string;
  instagramUrl: string;
  youtubeUrl: string;
  linkedInUrl: string;
  tiktokUrl: string;
  logoFile: File | null;
  bannerFile: File | null;
}

const emptyForm: OrganizationFormValues = {
  name: "",
  slug: "",
  organizationType: OrganizationType.Dental,
  phone: "",
  email: "",
  description: "",
  address: "",
  city: "",
  area: "",
  latitude: "",
  longitude: "",
  googleMapsUrl: "",
  websiteTitle: "",
  websiteTagline: "",
  facebookUrl: "",
  instagramUrl: "",
  youtubeUrl: "",
  linkedInUrl: "",
  tiktokUrl: "",
  logoFile: null,
  bannerFile: null,
};

const fromOrganization = (
  org: OrganizationResponse,
): OrganizationFormValues => ({
  name: org.name,
  slug: org.slug,
  organizationType: org.organizationType ?? OrganizationType.Dental,
  phone: org.phone || "",
  email: org.email || "",
  description: org.description || "",
  address: org.address || "",
  city: org.city || "",
  area: org.area || "",
  latitude: org.latitude !== null && org.latitude !== undefined
    ? String(org.latitude)
    : "",
  longitude: org.longitude !== null && org.longitude !== undefined
    ? String(org.longitude)
    : "",
  googleMapsUrl: org.googleMapsUrl || "",
  websiteTitle: org.websiteTitle || "",
  websiteTagline: org.websiteTagline || "",
  facebookUrl: org.facebookUrl || "",
  instagramUrl: org.instagramUrl || "",
  youtubeUrl: org.youtubeUrl || "",
  linkedInUrl: org.linkedInUrl || "",
  tiktokUrl: org.tiktokUrl || "",
  logoFile: null,
  bannerFile: null,
});

const toSlug = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const toUpdateOrganizationRequest = (
  form: OrganizationFormValues,
  current: OrganizationResponse,
): UpdateOrganizationRequest => ({
  name: form.name || undefined,
  slug: form.slug || undefined,
  organizationType: form.organizationType,
  phone: form.phone || undefined,
  email: form.email || undefined,
  description: form.description || undefined,
  address: form.address || undefined,
  city: form.city || undefined,
  area: form.area || undefined,
  latitude: form.latitude ? Number(form.latitude) : undefined,
  longitude: form.longitude ? Number(form.longitude) : undefined,
  googleMapsUrl: form.googleMapsUrl || undefined,
  facebookUrl: form.facebookUrl || undefined,
  instagramUrl: form.instagramUrl || undefined,
  youtubeUrl: form.youtubeUrl || undefined,
  linkedInUrl: form.linkedInUrl || undefined,
  tiktokUrl: form.tiktokUrl || undefined,
  websiteTitle: form.websiteTitle || undefined,
  websiteTagline: form.websiteTagline || undefined,
  logoFile: form.logoFile ?? undefined,
  bannerFile: form.bannerFile ?? undefined,
  isActive: current.isActive,
});

type TextKey = Exclude<
  keyof OrganizationFormValues,
  "logoFile" | "bannerFile" | "organizationType"
>;

const OrganizationModal = ({
  isOpen,
  mode,
  organization,
  submitting,
  onClose,
  onSubmit,
}: OrganizationModalProps) => {
  const [form, setForm] = useState<OrganizationFormValues>(emptyForm);

  const isReadOnly = mode === "view";

  useEffect(() => {
    if (!isOpen) return;
    if (mode === "create") {
      setForm(emptyForm);
    } else if (organization) {
      setForm(fromOrganization(organization));
    }
  }, [isOpen, mode, organization]);

  if (!isOpen) return null;

  const setField = (key: TextKey, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form);
  };

  const handleClose = () => {
    onClose();
  };

  const textField = (
    key: TextKey,
    label: string,
    opts?: { type?: string; placeholder?: string; full?: boolean },
  ) => (
    <div
      className={`org-form-group ${opts?.full ? "org-form-group--full" : ""}`}
    >
      <label htmlFor={`org-${key}`}>{label}</label>
      <input
        id={`org-${key}`}
        type={opts?.type ?? "text"}
        value={form[key]}
        placeholder={opts?.placeholder}
        disabled={isReadOnly}
        onChange={(e) => setField(key, e.target.value)}
      />
    </div>
  );

  return (
    <div className="org-modal-overlay" onClick={isReadOnly ? handleClose : undefined}>
      <div className="org-modal" onClick={(e) => e.stopPropagation()}>
        <div className="org-modal__header">
          <h3>
            {mode === "create"
              ? "Add Organization"
              : mode === "edit"
                ? "Edit Organization"
                : organization?.name ?? "Organization"}
          </h3>
          <button
            className="org-modal__close"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form className="org-modal__body" onSubmit={handleSubmit}>
          <h4 className="org-section-title">Basic Information</h4>
          <div className="org-form-grid">
            <div className="org-form-group">
              <label htmlFor="org-name">Name {mode === "create" && "*"}</label>
              <input
                id="org-name"
                type="text"
                value={form.name}
                disabled={isReadOnly}
                onChange={(e) => {
                  const name = e.target.value;
                  setField("name", name);
                  if (mode === "create") setField("slug", toSlug(name));
                }}
              />
            </div>

            <div className="org-form-group">
              <label htmlFor="org-slug">Slug {mode === "create" && "*"}</label>
              <input
                id="org-slug"
                type="text"
                value={form.slug}
                disabled={isReadOnly}
                onChange={(e) => setField("slug", toSlug(e.target.value))}
              />
            </div>

            <div className="org-form-group">
              <label htmlFor="org-type">Organization Type</label>
              <select
                id="org-type"
                value={String(form.organizationType)}
                disabled={isReadOnly}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    organizationType: Number(
                      e.target.value,
                    ) as OrganizationType,
                  }))
                }
              >
                {ORGANIZATION_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {textField("phone", "Phone", { type: "tel" })}
            {textField("email", "Email", { type: "email" })}
            {textField("city", "City")}
            {textField("area", "Area")}
            {textField("address", "Address", { full: true })}
            {textField("latitude", "Latitude", { type: "number" })}
            {textField("longitude", "Longitude", { type: "number" })}
            {textField("googleMapsUrl", "Google Maps URL", {
              full: true,
              placeholder: "https://maps.google.com/...",
            })}

            <div className="org-form-group org-form-group--full">
              <label htmlFor="org-description">Description</label>
              <textarea
                id="org-description"
                rows={3}
                value={form.description}
                disabled={isReadOnly}
                onChange={(e) => setField("description", e.target.value)}
              />
            </div>
          </div>

          <h4 className="org-section-title">Website Branding</h4>
          <div className="org-form-grid">
            {textField("websiteTitle", "Website Title")}
            {textField("websiteTagline", "Website Tagline")}
          </div>

          <h4 className="org-section-title">Social Links</h4>
          <div className="org-form-grid">
            {textField("facebookUrl", "Facebook URL")}
            {textField("instagramUrl", "Instagram URL")}
            {textField("youtubeUrl", "YouTube URL")}
            {textField("linkedInUrl", "LinkedIn URL")}
            {textField("tiktokUrl", "TikTok URL")}
          </div>

          <h4 className="org-section-title">Images</h4>
          <div className="org-form-grid">
            <div className="org-form-group">
              <label htmlFor="org-logo">Logo</label>
              <input
                id="org-logo"
                type="file"
                accept="image/*"
                disabled={isReadOnly}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    logoFile: e.target.files?.[0] ?? null,
                  }))
                }
              />
            </div>

            <div className="org-form-group">
              <label htmlFor="org-banner">Banner</label>
              <input
                id="org-banner"
                type="file"
                accept="image/*"
                disabled={isReadOnly}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    bannerFile: e.target.files?.[0] ?? null,
                  }))
                }
              />
            </div>
          </div>

          {!isReadOnly && (
            <div className="org-modal__actions">
              <button
                type="button"
                className="org-btn org-btn--secondary"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="org-btn org-btn--primary"
                disabled={submitting}
              >
                {submitting
                  ? "Saving..."
                  : mode === "create"
                    ? "Add Organization"
                    : "Save Changes"}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default OrganizationModal;